import React, { useState, useEffect } from 'react';
import api from '../../services/api';

interface GlobalStats {
  total_companies: number;
  active_companies: number;
  trial_companies: number;
  total_users: number;
  total_wheels: number;
  active_wheels: number;
  total_products: number;
  total_spins: number;
  spins_last_30_days: number;
  spins_last_7_days: number;
  active_companies_30_days: number;
  total_transactions?: number;
  total_revenue?: number;
  revenue_last_30_days?: number;
  revenue_last_7_days?: number;
  avg_transaction_value?: number;
}

interface StatsResponse {
  success: boolean;
  data: GlobalStats;
}

const GlobalStatsPage: React.FC = () => {
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await api.get<StatsResponse>('/admin/stats');
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">Carregando estatísticas...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <div className="text-center py-8 text-red-600">Erro ao carregar estatísticas</div>
      </div>
    );
  }

  const StatCard: React.FC<{ title: string; value: number | string; subtitle?: string; color?: string }> = ({ 
    title, 
    value, 
    subtitle,
    color = 'blue' 
  }) => {
    const colorClasses: Record<string, string> = {
      blue: 'bg-blue-50 border-blue-200',
      green: 'bg-green-50 border-green-200',
      purple: 'bg-purple-50 border-purple-200',
      yellow: 'bg-yellow-50 border-yellow-200',
      red: 'bg-red-50 border-red-200',
      indigo: 'bg-indigo-50 border-indigo-200'
    };

    return (
      <div className={`${colorClasses[color]} border-2 rounded-lg p-6`}>
        <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
        <p className="text-3xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
    );
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Estatísticas Globais do Sistema</h1>
        <p className="text-gray-600">Visão geral de todas as empresas e atividades</p>
      </div>

      {/* Empresas */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Empresas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            title="Total de Empresas"
            value={stats.total_companies}
            color="blue"
          />
          <StatCard
            title="Empresas Ativas"
            value={stats.active_companies}
            subtitle={`${Math.round((stats.active_companies / stats.total_companies) * 100)}% do total`}
            color="green"
          />
          <StatCard
            title="Empresas em Trial"
            value={stats.trial_companies}
            subtitle={`${Math.round((stats.trial_companies / stats.total_companies) * 100)}% do total`}
            color="yellow"
          />
        </div>
      </div>

      {/* Usuários e Conteúdo */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Usuários e Conteúdo</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Total de Usuários"
            value={stats.total_users}
            color="purple"
          />
          <StatCard
            title="Total de Roletas"
            value={stats.total_wheels}
            subtitle={`${stats.active_wheels} ativas`}
            color="indigo"
          />
          <StatCard
            title="Total de Produtos"
            value={stats.total_products}
            color="blue"
          />
          <StatCard
            title="Empresas Ativas (30d)"
            value={stats.active_companies_30_days}
            subtitle="Com spins nos últimos 30 dias"
            color="green"
          />
        </div>
      </div>

      {/* Leads/Spins */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Leads Capturados</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            title="Total de Leads"
            value={stats.total_spins.toLocaleString('pt-BR')}
            color="green"
          />
          <StatCard
            title="Últimos 30 Dias"
            value={stats.spins_last_30_days.toLocaleString('pt-BR')}
            subtitle={`${Math.round((stats.spins_last_30_days / stats.total_spins) * 100)}% do total`}
            color="blue"
          />
          <StatCard
            title="Últimos 7 Dias"
            value={stats.spins_last_7_days.toLocaleString('pt-BR')}
            subtitle={`${Math.round((stats.spins_last_7_days / stats.spins_last_30_days) * 100)}% dos últimos 30d`}
            color="indigo"
          />
        </div>
      </div>

      {/* Receita */}
      {stats.total_revenue !== undefined && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Receita</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              title="Receita Total"
              value={formatCurrency(stats.total_revenue)}
              color="green"
            />
            <StatCard
              title="Últimos 30 Dias"
              value={formatCurrency(stats.revenue_last_30_days || 0)}
              color="blue"
            />
            <StatCard
              title="Últimos 7 Dias"
              value={formatCurrency(stats.revenue_last_7_days || 0)}
              color="indigo"
            />
            <StatCard
              title="Ticket Médio"
              value={formatCurrency(stats.avg_transaction_value || 0)}
              color="purple"
            />
          </div>
        </div>
      )}

      {/* Métricas de Engajamento */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Métricas de Engajamento</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard
            title="Média de Leads/Empresa"
            value={(stats.total_spins / stats.total_companies).toFixed(1)}
            color="blue"
          />
          <StatCard
            title="Média de Roletas/Empresa"
            value={(stats.total_wheels / stats.total_companies).toFixed(1)}
            color="purple"
          />
          <StatCard
            title="Taxa de Ativação"
            value={`${Math.round((stats.active_companies_30_days / stats.total_companies) * 100)}%`}
            subtitle="Empresas com uso recente"
            color="green"
          />
        </div>
      </div>

      {/* Botão de atualizar */}
      <div className="flex justify-center mt-8">
        <button
          onClick={loadStats}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Atualizar Estatísticas
        </button>
      </div>
    </div>
  );
};

export default GlobalStatsPage;
